<script setup lang="ts">
import type { SpecieResourceDetailed, SpeciePullResource } from "@carnet/api/backoffice";

const props = defineProps<{ detail: SpecieResourceDetailed | SpeciePullResource }>();

const { t } = useI18n();
const slots = useSlots();
</script>

<template>
  <AppSection class="bg-elevated rounded-b-2xl" :ui="{ base: 'pt-8 md:pt-10 pb-6 md:pb-10' }">
    <div class="lg:text-lg/normal flex flex-col md:flex-row md:gap-10">
      <div class="order-2 md:order-1 md:basis-1/2 md:shrink-0">
        <figure class="sticky top-[calc(var(--ui-header-height)+(--spacing(4)))] bg-blue-300 flex items-center justify-center">
          <AppImage
            :src="detail.mainImage?.url"
            :alt="detail.commonName"
            variant="species"
            loading="eager"
            fetchpriority="high"
            img-class="w-full h-auto max-h-screen"
          >
            <template #placeholder>
              <AppImagePlaceholder variant="species" class="aspect-4/3 w-full" />
            </template>
          </AppImage>
        </figure>
      </div>

      <div class="contents md:flex md:flex-col md:basis-1/2 md:min-w-0 md:order-2">
        <header class="order-1 md:order-0 md:border-b border-blue-400 pb-10 flex flex-col gap-6 sm:gap-10">
          <div class="flex flex-col gap-2 sm:gap-4">
            <h1 class="text-5xl sm:text-8xl font-bold">{{ detail.commonName }}</h1>
            <p v-if="detail.scientificName || detail.discoveryLabel" class="text-lg sm:text-2xl text-blue-600">
              <i v-if="detail.scientificName">{{ detail.scientificName }}</i
              ><template v-if="detail.discoveryLabel && detail.scientificName"><br /></template>
              <template v-if="detail.discoveryLabel">{{ detail.discoveryLabel }}</template>
            </p>
          </div>
          <ul v-if="detail.taxonList.length" class="flex flex-wrap items-center gap-y-1" :aria-label="t('species.detail.taxonomy')">
            <li v-for="(taxon, index) in detail.taxonList.toReversed()" :key="index">
              {{ taxon }}<span v-if="index < detail.taxonList.length - 1" class="text-blue-400 px-3" aria-hidden="true">|</span>
            </li>
          </ul>
        </header>

        <AppSpecieDetailSections :detail="detail" class="order-3 md:order-0">
          <template v-if="slots['observations-map']" #observations>
            <slot name="observations-map" />
          </template>
        </AppSpecieDetailSections>
      </div>
    </div>
  </AppSection>
</template>
